import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Building2, Shield, Activity, TrendingUp, ArrowRight } from 'lucide-react';
import Logo from '../assets/logo.svg';
import toast from 'react-hot-toast';
import InsuranceNavbar from '../components/InsuranceNavbar';
import InsuranceFooter from '../components/InsuranceFooter';

const InsuranceLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      if (email && password) {
        toast.success('Welcome to HIRSTO Insurance Platform!');
        navigate('/dashboard');
      } else {
        toast.error('Please enter your credentials');
      }
      setIsLoading(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <InsuranceNavbar />

      {/* Login Content */}
      <div className="min-h-screen flex items-center justify-center p-4 py-20 relative overflow-hidden">
        <div className="w-full max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative z-10">
        
        {/* Left Side - Branding & Features */}
        <div className="text-center lg:text-left space-y-8 hidden lg:block">
          {/* Logo & Brand */}
          <div className="space-y-4">
            <div className="flex items-left justify-center lg:justify-start">
              <div>
                <img src={Logo} alt="HIRSTO" className="w-52 mb-2" />
                <p className="text-sm font-medium text-[#0077B6] tracking-wider">
                  EARLY DETECTION BETTER TOMORROW
                </p>
              </div>
            </div>
            <p className="text-base text-[#5C7A99] max-w-md mx-auto lg:mx-0 leading-relaxed">
              Your comprehensive healthcare platform for modern medical practice
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto lg:mx-0">
            {[
              { icon: Building2, title: "Patient Care", desc: "Advanced monitoring" },
              { icon: Shield, title: "Secure Data", desc: "HIPAA compliant" },
              { icon: Activity, title: "Team Collaboration", desc: "Seamless workflow" },
              { icon: TrendingUp, title: "Smart Analytics", desc: "AI-powered insights" }
            ].map((feature) => (
              <div
                key={feature.title}
                className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#0077B6]/20 hover:bg-white/90 hover:border-[#0077B6]/40 transition-all duration-300 hover:shadow-lg"
              >
                <feature.icon className="w-6 h-6 text-[#0077B6] mb-3" />
                <h3 className="font-semibold text-[#1E3A5F] text-sm mb-1">
                  {feature.title}
                </h3>
                <p className="text-xs text-[#5C7A99]">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 gap-8 max-w-lg mx-auto lg:mx-0 pt-4">
            <div className="text-center lg:text-left">
              <div className="text-3xl font-bold text-[#1E3A5F] mb-1">30+</div>
              <div className="text-sm text-[#5C7A99]">Founding Physicians</div>
            </div>
            <div className="text-center lg:text-left">
              <div className="text-3xl font-bold text-[#1E3A5F] mb-1">10hrs</div>
              <div className="text-sm text-[#5C7A99]">Saved Monthly</div>
            </div>
            <div className="text-center lg:text-left">
              <div className="text-3xl font-bold text-[#1E3A5F] mb-1">100%</div>
              <div className="text-sm text-[#5C7A99]">Evidence-Based</div>
            </div>
            <div className="text-center lg:text-left">
              <div className="text-3xl font-bold text-[#1E3A5F] mb-1">24/7</div>
              <div className="text-sm text-[#5C7A99]">Dedicated Assistance</div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-xl mx-auto lg:mx-0">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-[#03045E] px-8 py-8">
              <h2 className="text-3xl font-bold text-white mb-2">
                Welcome Back
              </h2>
              <p className="text-white/80 text-sm">
                Sign in to access your medical dashboard
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-10">
              <form onSubmit={handleLogin} className="space-y-5">
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all duration-200 bg-white placeholder:text-gray-400 text-gray-900"
                      placeholder="doctor@hospital.com"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all duration-200 bg-white text-gray-900"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#0077B6]/50 hover:text-[#0077B6] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Sign In Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#03045E] text-white py-4 rounded-xl font-semibold text-base hover:bg-[#02033d] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group mt-6"
                >
                  {isLoading ? (
                    <span>Signing in...</span>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              {/* Register Link */}
              <div className="mt-8 text-center">
                <p className="text-sm text-[#5C7A99]">
                  Don't have an account?{' '}
                  <Link
                    to="/dashboard/setup"
                    className="text-[#0077B6] hover:text-[#005a8a] font-semibold transition-colors"
                  >
                    Create Account
                  </Link>
                </p>
              </div>

              {/* Security Badge */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs text-center text-gray-400 flex items-center justify-center gap-2">
                  <Shield className="w-3.5 h-3.5" />
                  Protected by enterprise-grade security
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <InsuranceFooter />
    </div>
  );
};

export default InsuranceLogin;
